import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useId, useRef } from "react";
import "./PiSelect.css";

export interface PiSelectOption {
  value: string;
  label: string;
}

/**
 * Compact single-value select for property columns. The trigger matches a 36px field;
 * the menu is sized to the trigger and opens scrolled to the current value.
 * An empty `value` shows `placeholder`; `noneLabel` adds a row that clears the value.
 */
export default function PiSelect(props: {
  label: string;
  value: string;
  options: PiSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  noneLabel?: string;
  disabled?: boolean;
}) {
  const { label, value, options, onChange, placeholder, noneLabel, disabled } = props;
  const labelId = useId();
  const valueId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.value === value);

  const focusSelectedOnOpen = useRef(false);

  // On open Radix focuses the menu, then (keyboard open) its first item, within the same focus event.
  // Bring the current value into view, and take over that first-item focus so the value is highlighted.
  const handleMenuFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!focusSelectedOnOpen.current) return;
    const item = contentRef.current?.querySelector<HTMLElement>('[data-state="checked"]');
    if (event.target === event.currentTarget) {
      item?.scrollIntoView?.({ block: "nearest" });
      queueMicrotask(() => { focusSelectedOnOpen.current = false; });
      return;
    }
    focusSelectedOnOpen.current = false;
    if (!item || item === event.target) return;
    item.focus();
    item.scrollIntoView?.({ block: "nearest" });
  };

  return (
    <div className="pi-select">
      <span id={labelId} className="pi-select__label">{label}</span>
      <DropdownMenu.Root onOpenChange={(open) => { focusSelectedOnOpen.current = open; }}>
        <DropdownMenu.Trigger className="pi-select__trigger" disabled={disabled} aria-labelledby={`${labelId} ${valueId}`}>
          <span id={valueId} className={selected ? "pi-select__value" : "pi-select__value pi-select__value--placeholder"}>
            {selected?.label ?? placeholder}
          </span>
          <ExpandMoreIcon className="pi-select__chevron" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            ref={contentRef}
            className="pi-select__menu pit-creator-scrollbar"
            side="bottom"
            align="start"
            sideOffset={4}
            collisionPadding={8}
            onFocus={handleMenuFocus}
          >
            <DropdownMenu.RadioGroup value={value} onValueChange={onChange}>
              {noneLabel && <>
                <DropdownMenu.RadioItem value="" className="pi-select__item pi-select__item--none">
                  {noneLabel}
                </DropdownMenu.RadioItem>
                {options.length > 0 && <DropdownMenu.Separator className="pi-select__separator" />}
              </>}
              {options.map((option) => (
                <DropdownMenu.RadioItem key={option.value} value={option.value} textValue={option.label}
                  title={option.label} className="pi-select__item">
                  <span className="pi-select__item-label">{option.label}</span>
                  <DropdownMenu.ItemIndicator className="pi-select__check">
                    <CheckIcon />
                  </DropdownMenu.ItemIndicator>
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
